import {
  NavigationProp,
  ParamListBase,
  useNavigation,
} from "@react-navigation/native";
import { useAtomValue } from "jotai";
import { useCallback } from "react";

import { navigationRef_atom } from "@/features/Navigation";
import {
  getNavigationForRoute,
  RouteLookupNavigation,
} from "@/features/Navigation/utils/getNavigationForRoute";
import { sessionCurrentClubSlugAtom } from "@/features/Session";
import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator";

export const useScheduleNavigator = (
  navigationOverride?: RouteLookupNavigation
) => {
  const navigationRef = useAtomValue(navigationRef_atom);
  const clubSlug = useAtomValue(sessionCurrentClubSlugAtom);
  const contextNavigation = useNavigation<NavigationProp<ParamListBase>>();
  const navigation = navigationOverride ?? contextNavigation;

  const getClubNavigation = useCallback(
    () =>
      navigationOverride?.getParent?.<
        NavigationProp<ClubStackNavigatorParamList>
      >() ??
      getNavigationForRoute<ClubStackNavigatorParamList>(navigation, "Agenda"),
    [navigation, navigationOverride]
  );

  const navigateToAgenda = useCallback(
    (facilitySlug: string, date: string) => {
      if (clubSlug && navigationRef?.isReady()) {
        navigationRef.navigate("Club", {
          clubSlug,
          screen: "Agenda",
          params: { date, facilitySlug },
        });
        return;
      }

      getClubNavigation().navigate("Agenda", { date, facilitySlug });
    },
    [clubSlug, navigationRef, getClubNavigation]
  );

  const navigateToDepartmentAvailability = useCallback(
    (departmentSlug: string, date: string) => {
      if (clubSlug && navigationRef?.isReady()) {
        navigationRef.navigate("Club", {
          clubSlug,
          screen: "DepartmentAvailability",
          params: {
            date,
            departmentSlug,
          },
        });
        return;
      }

      getClubNavigation().navigate("DepartmentAvailability", {
        date,
        departmentSlug,
      });
    },
    [clubSlug, navigationRef, getClubNavigation]
  );

  const navigate = useCallback(
    (type: "facility" | "department", slug: string, date: string) => {
      if (type === "facility") {
        navigateToAgenda(slug, date);
      } else if (type === "department") {
        navigateToDepartmentAvailability(slug, date);
      }
    },
    [navigateToAgenda, navigateToDepartmentAvailability]
  );

  return navigate;
};
